import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

type DataProviderListItem = {
  id: string;
  name: string;
};

type VisualizationListItem = {
  id: string;
  name: string;
  description: string | null;
  data_provider_id: string;
  data_provider_name: string;
  connector_id: string | null;
  connector_name: string | null;
  updated_at: string;
};

export function Visualizations() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<VisualizationListItem[]>([]);
  const [providers, setProviders] = useState<DataProviderListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createProviderId, setCreateProviderId] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [visData, providersData] = await Promise.all([
        apiFetch<{ visualizations: VisualizationListItem[] }>("/api/tenant/visualizations"),
        apiFetch<{ data_providers: DataProviderListItem[] }>("/api/tenant/data-providers")
      ]);
      setRows(visData.visualizations);
      setProviders(providersData.data_providers);
      setSelectedId((current) =>
        current && visData.visualizations.some((v) => v.id === current) ? current : visData.visualizations[0]?.id ?? null
      );
      if (!createProviderId && providersData.data_providers[0]?.id) {
        setCreateProviderId(providersData.data_providers[0].id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const selected = useMemo(() => rows.find((v) => v.id === selectedId) ?? null, [rows, selectedId]);

  async function createVisualization() {
    const name = createName.trim();
    if (!name || !createProviderId) return;
    setSaving(true);
    try {
      const data = await apiFetch<{ visualization: { id: string } }>("/api/tenant/visualizations", {
        method: "POST",
        body: {
          name,
          description: createDescription.trim() || null,
          data_provider_id: createProviderId
        }
      });
      setCreateOpen(false);
      setCreateName("");
      setCreateDescription("");
      nav(`/app/build/visualizations/${data.visualization.id}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function duplicateSelected() {
    if (!selected) return;
    try {
      const data = await apiFetch<{ visualization: { id: string } }>(
        `/api/tenant/visualizations/${encodeURIComponent(selected.id)}/duplicate`,
        { method: "POST", body: {} }
      );
      await load();
      setSelectedId(data.visualization.id);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function deleteSelected() {
    if (!selected) return;
    const ok = await confirm.confirm({
      title: "Delete visualization?",
      detail: `This deletes "${selected.name}".`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/visualizations/${encodeURIComponent(selected.id)}`, { method: "DELETE" });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <PageLayout
      title="Visualizations"
      actions={
        <>
          <button className="btn" type="button" onClick={() => void duplicateSelected()} disabled={!selected}>
            Duplicate
          </button>
          <button className="btn" type="button" onClick={() => void deleteSelected()} disabled={!selected}>
            Delete
          </button>
          <button className="btn btnPrimary" type="button" onClick={() => setCreateOpen(true)} disabled={providers.length === 0}>
            New visualization
          </button>
        </>
      }
    >
      <div className="card">
        {loading ? <LoadingState /> : null}
        {!loading && error ? <ErrorState detail={error} /> : null}
        {!loading && !error && rows.length === 0 ? <div className="muted">No visualizations</div> : null}
        {!loading && !error && rows.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th />
                  <th>Name</th>
                  <th>Data Provider</th>
                  <th>Connector</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} onClick={() => setSelectedId(row.id)}>
                    <td>
                      <input type="radio" checked={selectedId === row.id} onChange={() => setSelectedId(row.id)} aria-label={`Select ${row.name}`} />
                    </td>
                    <td>
                      <button className="linkBtn" type="button" onClick={() => nav(`/app/build/visualizations/${row.id}`)}>
                        {row.name}
                      </button>
                    </td>
                    <td>{row.data_provider_name}</td>
                    <td>{row.connector_name ?? "Unassigned"}</td>
                    <td>{new Date(row.updated_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="modalOverlay" role="presentation" onClick={() => setCreateOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="reportModalTitle">New visualization</div>
            <div className="field">
              <label className="label" htmlFor="new-vis-name">
                Name
              </label>
              <input id="new-vis-name" className="input" value={createName} onChange={(e) => setCreateName(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label className="label" htmlFor="new-vis-description">
                Description
              </label>
              <textarea
                id="new-vis-description"
                className="input reportTextarea"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="new-vis-provider">
                Data provider
              </label>
              <select id="new-vis-provider" className="input" value={createProviderId} onChange={(e) => setCreateProviderId(e.target.value)}>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="actions">
              <button className="btn" type="button" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={() => void createVisualization()} disabled={saving || createName.trim() === "" || !createProviderId}>
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
